import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Alert, Button, Divider, Flex, Input, notification } from "antd";

import { api } from "../../api";
import AuthCard from "../../components/auth/AuthCard";
import { GoogleSignInButton } from "../../components/auth/GoogleSignInButton";
import { YandexSignInButton } from "../../components/auth/YandexSignInButton";
import { YANDEX_COUNTRIES } from "../../config";
import { loginStore } from "../../stores/loginStore";
import { usePageTitle } from "../../utils/usePageTitle";
import { usePendingAction } from "../../utils/usePendingAction";
import { validateEmail } from "../../utils/validateEmail";

import "./LoginPage.css";

import type { CheckUserResponse } from "../../api/auth";

export const LoginPage = () => {
  const { t, i18n } = useTranslation();
  usePageTitle(t("signin.title"));
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(
    (location.state as { email?: string })?.email ?? "",
  );
  const [result, setResult] = useState<CheckUserResponse | null>(null);
  const { pending: loading, run: runContinue } = usePendingAction();
  const [emailError, setEmailError] = useState("");
  const [geoResolved, setGeoResolved] = useState(false);
  const [showGoogle, setShowGoogle] = useState(false);
  const [showYandex, setShowYandex] = useState(false);

  useEffect(() => {
    api.countries
      .getCurrentCountry(i18n.language)
      .then((current) => {
        setShowGoogle(current.code !== "RU");
        setShowYandex(
          current.code !== null && YANDEX_COUNTRIES.has(current.code),
        );
      })
      .catch(() => {
        setShowGoogle(true);
        setShowYandex(false);
      })
      .finally(() => {
        setGeoResolved(true);
      });
  }, [i18n.language]);

  async function handleContinue() {
    if (!email || !validateEmail(email)) {
      setEmailError(t("errors.shared.invalid_email"));
      return;
    }
    await runContinue(async () => {
      setEmailError("");
      setResult(null);
      try {
        const data = await api.auth.checkUser(email);

        if (data.status === "exists") {
          loginStore.setEmail(email);
          loginStore.setNext(searchParams.get("next") ?? "");
          navigate("/login/2");
          return;
        }

        // TODO: show user-friendly error message for email alias conflict (MY-579)
        if (data.status === "conflict") {
          console.log("email conflict");
          return;
        }

        setResult(data);
      } catch {
        notification.error({
          message: t("errors.shared.common_error"),
          placement: "topRight",
        });
      }
    });
  }

  return (
    <AuthCard>
      <h1>{t("signin.title")}</h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleContinue();
        }}
      >
        <Input
          type="text"
          name="email"
          placeholder={t("app.yourEmail")}
          allowClear
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError("");
          }}
          onPressEnter={handleContinue}
          autoComplete="username"
          size="large"
          status={emailError ? "error" : undefined}
        />
        {emailError && <div className="email-error">{emailError}</div>}

        {result && (
          <Alert
            title={
              result.status === "exists"
                ? t("result.userOk")
                : t("errors.signin.user_absent")
            }
            type={result.status === "exists" ? "success" : "error"}
            showIcon
            style={{ marginTop: 16 }}
          />
        )}

        <Flex gap={12} align="center" wrap="wrap" style={{ marginTop: 16 }}>
          <Button
            type="primary"
            size="large"
            loading={loading}
            onClick={handleContinue}
            className="login-actions__continue"
          >
            {t("app.continue")}
          </Button>
          <Button
            size="large"
            onClick={() => {
              const next = searchParams.get("next");
              navigate(
                `/signup/0${next ? `?next=${encodeURIComponent(next)}` : ""}`,
                { state: { email } },
              );
            }}
            className="login-actions__signup"
          >
            {t("signup.title")}
          </Button>
          <span className="login-actions__spacer" />
          <Link
            to="/password/reset"
            state={{ email }}
            className="login-actions__forgot"
          >
            {t("signin.forgotPassword")}
          </Link>
        </Flex>
      </form>

      {geoResolved && (
        <>
          <Divider plain className="divider-or">
            {t("app.or")}
          </Divider>
          <Flex vertical gap={8}>
            {showGoogle && <GoogleSignInButton />}
            {showYandex && <YandexSignInButton />}
          </Flex>
        </>
      )}

      <div className="login-mobile-links">
        <Link
          to={`/signup/0${searchParams.get("next") ? `?next=${encodeURIComponent(searchParams.get("next")!)}` : ""}`}
          state={{ email }}
        >
          {t("signup.title")}
        </Link>
        <br />
        <Link to="/password/reset" state={{ email }}>
          {t("signin.forgotPassword")}
        </Link>
      </div>
    </AuthCard>
  );
};
